/**
 * The payment list's one read, and what it depends on (Rechnungen 2.0 — R6b).
 *
 * ONE REQUEST, NOT TWO. The invoice workspace asks twice because its four cards
 * describe the population BEFORE the table's status filter. Payments have no
 * such split and never will: the Bexio mirror is upsert-only and deletes
 * nothing, so any aggregate over these rows overstates cash by construction.
 * There is no summary endpoint to call and no total to fold — this hook counts
 * ROWS, and the count comes from the server.
 *
 * `active` IS HOW A WORKSPACE STAYS QUIET. The page holds two workspaces and
 * calls both hooks on every render, because Rules of Hooks leave no choice. "Not
 * on screen" therefore has to be a parameter rather than an absent call: while
 * it is false this hook asks nothing, holds nothing, and reports neither loading
 * nor failure.
 *
 * `native` IS ASKED, NOT ASSUMED. A payment request goes out under
 * `source="native"` too, and the answer is the server's `availability`. Deriving
 * `not_recorded` from the source would put a second copy of that rule in the
 * browser, free to drift from the backend's — and the request is free: the
 * native path issues no database query at all.
 *
 * THE OFFSET IS RESET DURING RENDER, not from an effect, for the same reason as
 * in `useInvoiceDashboardData`: an effect would let one request go out with the
 * new filter and the old page number, so every filter change would cost two
 * requests and the first would be discarded.
 *
 * NOTHING IS FILTERED, SORTED OR COLLAPSED HERE. The rows arrive in the order
 * and the quantity the server chose and are handed on untouched. Two payments
 * against one invoice are two events — production holds invoices whose two rows
 * sum to twice the invoice total, and collapsing them would show one plausible
 * payment instead of two questionable ones.
 */
import { useCallback, useMemo, useState } from "react";

import { fetchInvoiceDashboardPayments } from "../../../api/adminData/invoiceDashboard";
import { useDebouncedValue } from "../../../components/admin/bexio/workspace/useDebouncedValue";
import {
  defaultInvoicePeriod,
  periodToRange,
  useGuardedRead,
} from "./useInvoiceDashboardData";

/** The page size the reader gets before choosing one. */
export const PAYMENTS_DEFAULT_LIMIT = 25;

/** Newest payment first — the only ordering that needs no explanation. */
export const PAYMENTS_DEFAULT_SORT = "payment_date";
export const PAYMENTS_DEFAULT_DIRECTION = "desc";

/**
 * The two columns R6a will order by.
 *
 * The backend resolves the sort key from a closed literal tuple, so a third
 * header would produce a 422 on click rather than a differently ordered page.
 */
export const PAYMENTS_SORTABLE_FIELDS = ["payment_date", "amount"];

export default function useInvoicePaymentsData({ source, active = true } = {}) {
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState(defaultInvoicePeriod);
  const [sort, setSort] = useState(PAYMENTS_DEFAULT_SORT);
  const [direction, setDirection] = useState(PAYMENTS_DEFAULT_DIRECTION);
  const [limit, setLimit] = useState(PAYMENTS_DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);

  const debouncedQ = useDebouncedValue(q);
  // The same two dates the invoice list uses, bounding a different column: here
  // they are the day the money arrived.
  const { fromDate, toDate } = useMemo(() => periodToRange(period), [period]);

  // Everything that changes WHICH payments are described, as opposed to which
  // page of them is shown. A change to any of these puts the reader back on the
  // first page — page three of a different result set is not a place.
  const populationKey = JSON.stringify({
    source,
    debouncedQ,
    fromDate,
    toDate,
    sort,
    direction,
  });
  const [seenPopulation, setSeenPopulation] = useState(populationKey);
  if (seenPopulation !== populationKey) {
    setSeenPopulation(populationKey);
    setOffset(0);
  }
  const effectiveOffset = seenPopulation === populationKey ? offset : 0;

  const enabled = active && (source === "bexio" || source === "native");

  const params = useMemo(
    () => ({
      source,
      q: debouncedQ || undefined,
      fromDate,
      toDate,
      sort,
      direction,
      limit,
      offset: effectiveOffset,
    }),
    [source, debouncedQ, fromDate, toDate, sort, direction, limit, effectiveOffset]
  );

  const read = useGuardedRead({
    fetcher: fetchInvoiceDashboardPayments,
    params,
    enabled,
    failureMessage: "Zahlungen konnten nicht geladen werden.",
  });

  const toggleSort = useCallback(
    (field) => {
      if (!PAYMENTS_SORTABLE_FIELDS.includes(field)) return;
      // A new column starts at its most useful end — largest amount, most
      // recent date — and only a second click on the SAME column reverses it.
      if (sort === field) {
        setDirection((d) => (d === "desc" ? "asc" : "desc"));
        return;
      }
      setSort(field);
      setDirection(PAYMENTS_DEFAULT_DIRECTION);
    },
    [sort]
  );

  const reset = useCallback(() => {
    setQ("");
    setPeriod(defaultInvoicePeriod());
  }, []);

  const isFiltered = Boolean(debouncedQ || fromDate);

  return {
    q,
    debouncedQ,
    setQ,
    period,
    setPeriod,
    fromDate,
    toDate,
    sort,
    direction,
    toggleSort,
    limit,
    setLimit,
    offset: effectiveOffset,
    setOffset,
    isFiltered,
    reset,
    list: {
      items: Array.isArray(read.data?.items) ? read.data.items : [],
      total: typeof read.data?.total === "number" ? read.data.total : 0,
      // The server's word for "this source records no individual payments".
      // Null while nothing has been read yet, so a caller can tell "not asked"
      // from "asked and told".
      availability: read.data?.availability ?? null,
      loading: read.loading,
      error: read.error,
      reload: read.reload,
    },
  };
}
